/* eslint-disable class-methods-use-this */
import ReduceStore from "flux/lib/FluxReduceStore";

import ComponentAnnotation from "../../../foundation/model/ComponentAnnotation";

import AppActionTypes from "../app/AppActionTypes";
import BarrelActionTypes from "../barrel/BarrelActionTypes";
import ScreenActionTypes from "../screen/ScreenActionTypes";

import { AllPayloads } from "../payloads";

import * as Payloads from "./ComponentAnnotationsActionPayloads";
import ComponentAnnotationsActionTypes from "./ComponentAnnotationsActionTypes";
import { ComponentAnnotationsRecord } from "./types";

interface State {
    isGetComponentAnnotationsPending: boolean;
    componentAnnotationsRecord: ComponentAnnotationsRecord;
}

class ComponentAnnotationsDataStore extends ReduceStore<State, AllPayloads> {
    getInitialState(): State {
        return {
            isGetComponentAnnotationsPending: true,
            componentAnnotationsRecord: {}
        };
    }

    #getComponentAnnotations(state: State, pid: string): ComponentAnnotation[] | undefined {
        return state.componentAnnotationsRecord[pid];
    }

    reset(state: State): State {
        return {
            ...this.getInitialState(),
            componentAnnotationsRecord: state.componentAnnotationsRecord
        };
    }

    getComponentAnnotationsSuccess(state: State, {
        pid,
        componentAnnotations
    }: Payloads.GetComponentAnnotationsSuccess): State {
        return {
            ...state,
            componentAnnotationsRecord: {
                ...state.componentAnnotationsRecord,
                [pid]: componentAnnotations
            },
            isGetComponentAnnotationsPending: false
        };
    }

    getComponentAnnotationsFinish(state: State): State {
        return {
            ...state,
            isGetComponentAnnotationsPending: false
        };
    }

    addComponentAnnotation(state: State, {
        pid,
        annotation
    }: Payloads.AddComponentAnnotation): State {
        const { componentSourceId } = annotation;

        if (!componentSourceId) {
            return state;
        }

        return {
            ...state,
            componentAnnotationsRecord: {
                ...state.componentAnnotationsRecord,
                [pid]: [...(this.#getComponentAnnotations(state, pid) || []), annotation]
            }
        };
    }

    updateComponentAnnotation(state: State, {
        pid,
        atid,
        annotationData
    }: Payloads.UpdateComponentAnnotation): State {
        const { componentSourceId } = annotationData;

        if (!componentSourceId) {
            return state;
        }

        return {
            ...state,
            componentAnnotationsRecord: {
                ...state.componentAnnotationsRecord,
                [pid]: this.#getComponentAnnotations(state, pid)?.map(annotation => {
                    if (annotation._id !== atid) {
                        return annotation;
                    }

                    return { ...annotation, ...annotationData };
                })
            }
        };
    }

    updateComponentAnnotationType(state: State, {
        pid,
        atid,
        newType,
        componentSourceId
    }: Payloads.UpdateComponentAnnotationType): State {
        if (!componentSourceId) {
            return state;
        }

        return {
            ...state,
            componentAnnotationsRecord: {
                ...state.componentAnnotationsRecord,
                [pid]: this.#getComponentAnnotations(state, pid)?.map(annotation => {
                    if (annotation._id !== atid) {
                        return annotation;
                    }

                    return { ...annotation, type: newType };
                })
            }
        };
    }

    unsetComponentAnnotationType(state: State, {
        pid,
        atid,
        componentSourceId
    }: Payloads.UnsetComponentAnnotationType): State {
        if (!componentSourceId) {
            return state;
        }

        return {
            ...state,
            componentAnnotationsRecord: {
                ...state.componentAnnotationsRecord,
                [pid]: this.#getComponentAnnotations(state, pid)?.map(annotation => {
                    if (annotation._id !== atid) {
                        return annotation;
                    }

                    return { ...annotation, type: undefined };
                })
            }
        };
    }

    updateComponentAnnotationText(state: State, {
        pid,
        atid,
        componentSourceId,
        annotationData
    }: Payloads.UpdateComponentAnnotationText): State {
        if (!componentSourceId) {
            return state;
        }

        return {
            ...state,
            componentAnnotationsRecord: {
                ...state.componentAnnotationsRecord,
                [pid]: this.#getComponentAnnotations(state, pid)?.map(annotation => {
                    if (annotation._id !== atid) {
                        return annotation;
                    }

                    return { ...annotation, ...annotationData };
                })
            }
        };
    }

    updateComponentAnnotationPosition(state: State, {
        pid,
        atid,
        x, y,
        originalX, originalY
    }: Payloads.UpdateComponentAnnotationPosition): State {
        return {
            ...state,
            componentAnnotationsRecord: {
                ...state.componentAnnotationsRecord,
                [pid]: this.#getComponentAnnotations(state, pid)?.map(annotation => {
                    if (annotation._id !== atid) {
                        return annotation;
                    }

                    if ("originalCoordinates" in annotation && annotation.originalCoordinates) {
                        const { originalCoordinates: _, ...rest } = annotation;

                        return typeof originalX === "number" ? {
                            ...annotation,
                            originalCoordinates: { ...annotation.originalCoordinates, x: originalX, y: originalY! },
                            x,
                            y
                        } : {
                            ...rest,
                            x,
                            y
                        };
                    }

                    return { ...annotation, x, y };
                })
            }
        };
    }

    deleteComponentAnnotation(state: State, {
        pid,
        atid
    }: Payloads.DeleteComponentAnnotation): State {
        return {
            ...state,
            componentAnnotationsRecord: {
                ...state.componentAnnotationsRecord,
                [pid]: this.#getComponentAnnotations(state, pid)?.filter(annotation => annotation._id !== atid)
            }
        };
    }

    reduce(state: State, action: AllPayloads): State {
        switch (action.type) {
            case AppActionTypes.RESET:
            case BarrelActionTypes.RESET:
                return this.reset(state);

            case ComponentAnnotationsActionTypes.GET_COMPONENT_ANNOTATIONS_SUCCESS:
                return this.getComponentAnnotationsSuccess(state, action);
            case ComponentAnnotationsActionTypes.GET_COMPONENT_ANNOTATIONS_FINISH:
                return this.getComponentAnnotationsFinish(state);

            case ScreenActionTypes.ADD_ANNOTATION:
                return this.addComponentAnnotation(state, action as Payloads.AddComponentAnnotation);
            case ScreenActionTypes.UPDATE_ANNOTATION:
                return this.updateComponentAnnotation(state, action);
            case ScreenActionTypes.UPDATE_ANNOTATION_TYPE:
                return this.updateComponentAnnotationType(state, action);
            case ScreenActionTypes.DELETE_ANNOTATION_TYPE:
                return this.unsetComponentAnnotationType(state, action);
            case ScreenActionTypes.UPDATE_ANNOTATION_TEXT:
                return this.updateComponentAnnotationText(state, action);
            case ScreenActionTypes.UPDATE_ANNOTATION_POSITION:
                return this.updateComponentAnnotationPosition(state, action);
            case ScreenActionTypes.REMOVE_ANNOTATION:
            case ScreenActionTypes.UNLINK_ANNOTATION:
                return this.deleteComponentAnnotation(state, action);

            default:
                return state;
        }
    }
}

export default ComponentAnnotationsDataStore;
export {
    State as ComponentAnnotationsDataStoreState
};
